import { onUnmounted, ref } from 'vue';

export interface ObserverOptions {
  root: HTMLElement;
  rootMargin?: string;
  threshold?: number;
}

export const useIntersectionObserver = () => {
  const observerRef = ref<IntersectionObserver>();


  const createObserver = (options: ObserverOptions, callback: IntersectionObserverCallback): IntersectionObserver => {
    const opts = {
      ...{ rootMargin: '0px', threshold: 1.0 },
      ...options,
    };

    return new IntersectionObserver(callback, opts);
  };

  const getObserver = () => observerRef.value;

  const observe = (
    root: HTMLElement,
    watch: HTMLElement | Array<HTMLElement>,

    callback: IntersectionObserverCallback,
    rootMargin: string = '0px',
    threshold: number = 1.0,
  ): void => {
    const opts = {
      root: root,
      rootMargin: rootMargin,
      threshold: threshold,
    };

    const observer = createObserver(opts, callback);
    observerRef.value = observer;

    if (watch && !Array.isArray(watch)) {
      observer.observe(watch);
    }

    if (Array.isArray(watch)) {
      watch.forEach(el => observer.observe(el));
    }
  };

  const unobserve = (target: HTMLElement | Array<HTMLElement>): void => {
    if (typeof observerRef?.value?.unobserve !== 'function') return;
    if (Array.isArray(target)) {
      target.forEach(el => observerRef.value?.unobserve(el));
    } else if (target) {
      observerRef.value.unobserve(target);
    }
  };

  const cleanup = (): void => {
    if (typeof observerRef?.value?.disconnect === 'function') {
      observerRef.value.disconnect();
    }
  };

  onUnmounted(() => cleanup());

  return {
    cleanup,
    getObserver,
    observe,
    unobserve,
  };
};
